/**
 * "I will send you a quote" - the button that actually sends it.
 *
 * The commonest next step a sales call produces is a letter, and until now that
 * letter was written from a blank box beside a row already holding the summary,
 * the agreed steps and what the customer was unsure about. Everything the first
 * paragraph needed was one screen away and had to be remembered instead.
 *
 * Two states, and they are two different costs rather than two labels. The first
 * press writes the letter, which is one model request charged to the telephony
 * budget. Every press after that opens the letter that already exists, for
 * nothing - so an operator who drafts one, gets pulled onto another call and
 * comes back twenty minutes later finds their text rather than paying for it a
 * second time.
 *
 * ⚠ THE COMPOSER LIVES IN THE MAIL SCREEN, so this navigates rather than opening
 * anything. `/email?call=<id>` is the same hand-over the development brain's
 * letter uses (`?brain=<domain>`), and for the same reason: the recipient is
 * decided by the server on THAT request, not carried across from this one. A
 * company that unsubscribed in the meantime arrives with no address.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { followUpErrorKey, writeCallFollowUp } from "../../../lib/callFollowUp";
import { Btn, useToast } from "../../ui";

export function CallFollowUp({
  callId,
  hasSummary,
  hasDraft,
}: {
  callId: string;
  /** Nothing to write from means no button - the server refuses it anyway. */
  hasSummary: boolean;
  /** A letter already exists on this call, so opening it costs nothing. */
  hasDraft: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!hasSummary) return null;

  async function press() {
    // Already written: straight to the composer, no request spent. The mail
    // screen fetches the letter itself, address and all.
    if (hasDraft) {
      navigate(`/email?call=${encodeURIComponent(callId)}`);
      return;
    }
    setBusy(true);
    try {
      await writeCallFollowUp(callId);
      navigate(`/email?call=${encodeURIComponent(callId)}`);
    } catch (err) {
      // Named rather than blanket where the server named it: "there is nothing
      // written about this call yet" sends the operator to press transcribe,
      // where "AI error" sends them to the settings page.
      toast(t(followUpErrorKey(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-6 pb-3">
      <Btn small onClick={() => void press()} disabled={busy}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
        {t(hasDraft ? "calls.followUp.open" : "calls.followUp.write")}
      </Btn>
    </div>
  );
}
